import Config from "../Config";
import { GD } from "../GD";
import { isPrivateEndpointAllowed } from "./privateEndpoints";
import { fetchFresh } from "./fetchFresh";
import { withActiveIssuer } from "./issuerRequestContext";

// Pre-auth calls: a stale stored session must not attach its token or trip the private guard.
const PUBLIC_ENDPOINTS = new Set([
	"companies/referral/check",
	"users/login",
	"users/register",
	"users/register/confirm",
	"users/register/resend",
	"users/password/recover",
	"users/password/verify",
	"users/password/reset",
	"users/2fa/validate",
	"users/invites/signup",
]);
const TEMPORARY_SERVICE_STATUSES = new Set([502, 503, 504]);
const TEMPORARY_SERVICE_MESSAGE = "The service is temporarily unavailable. Please try again later.";

// Read-only endpoints: identical concurrent calls share one in-flight request.
const READ_ENDPOINT = /(^|\/)(list|get|issuers|stats|status|all)(\/|\?|$)/;

const isPublicEndpoint = (req: IAPIRequest) => {
	if (!req.method || req.url) return false;
	return PUBLIC_ENDPOINTS.has(req.method.split("?", 1)[0].replace(/^\/+/, ""));
};

class HTTP {
	busy: boolean = false;
	private activeBusyRequests = 0;
	private inFlightReads = new Map<string, Promise<IAPIResponse>>();

	constructor() {
		GD.REQ_BASIC.listener = async req => this.request(req);
		GD.REQ_API.listener = async req => this.requestAPI(req);
		GD.S_HTTP_BUSY.onSubscribe = cb => {
			cb!(this.busy);
		};
	}

	requestAPI(req: IAPIRequest): Promise<IAPIResponse> {
		req = withActiveIssuer(req);
		if (req.files?.length || !req.method || !READ_ENDPOINT.test(req.method)) return this.sendAPI(req);

		const key = JSON.stringify([req.url, req.method, req.httpMethod, req.params, req.data]);
		const pending = this.inFlightReads.get(key);
		if (pending) return pending;
		const request = this.sendAPI(req).finally(() => this.inFlightReads.delete(key));
		this.inFlightReads.set(key, request);
		return request;
	}

	private async sendAPI(req: IAPIRequest): Promise<IAPIResponse> {
		const publicEndpoint = isPublicEndpoint(req);
		let apiURL = Config.scheme.URL_API;
		if (req.apiVersion === 2) apiURL = Config.scheme.URL_API;
		const url = req.url ?? apiURL + req.method;
		const headers = req.headers ?? {};

		// ATTACH AUTH HEADER
		const token = publicEndpoint ? "" : await GD.REQ_AUTH_TOKEN.request();
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		// PRIVATE-COMPANY ENDPOINT ALLOWLIST
		// Private accounts may only call a fixed set of endpoints; while onboarding
		// (KYC not approved) the set is even narrower. Block the rest here so we
		// never fire a request the backend would just 403.
		if (!publicEndpoint && token && req.method && !req.url) {
			const company = await GD.REQ_COMPANY.request();
			if (company?.type === "private") {
				const onboarding = await GD.REQ_ONBOARDING.request();
				// `required` is the private onboarding gate. Legacy kyc_status now
				// mirrors the provider status and must not restrict app access.
				const gated = !!onboarding?.required;
				if (!isPrivateEndpointAllowed(req.method, gated)) {
					return {
						error: {
							code: 403,
							message: "Not available for this account",
							details: `"${req.method}" is not permitted for a private ${
								gated ? "account during onboarding" : "account"
							}.`,
						},
					};
				}
			}
		}

		// clear markups
		if (req.data && typeof req.data === "object") {
			req.data = this.clearMarkup(JSON.parse(JSON.stringify(req.data ?? {})));
		}

		// create formData if files exists
		let endData: FormData | Record<string, any> = req.data as any;
		if (req.files && req.files.length) {
			endData = this.jsonToFormData(req.data as any) as FormData;
			// The browser must add the multipart boundary to Content-Type.
			delete headers["Content-Type"];
			delete headers["content-type"];
			for (const f of req.files) endData.append("files", f);
		}

		const result = await this.request({
			url,
			method: req.httpMethod ?? "POST",
			data: endData,
			headers,
			params: req.params,
		});

		//!TODO:;
		if (result.error) {
			if (result.error.code === 6) GD.S_AUTH_LOG_OUT.invoke();
			return { error: result.error };
		}

		if (result.data && result.data.error) return { error: result.data.error };

		if (!result.data)
			return {
				error: { code: -1, message: "Empty response" },
			};

		return { data: result.data };
	}

	clearMarkup(data: any): any {
		if (data && typeof data === "object") {
			for (const k in data) {
				if (k.startsWith("$__")) {
					delete data[k];
					continue;
				}
				if (typeof data[k] === "object") data[k] = this.clearMarkup(data[k]);
			}
		}
		return data;
	}

	async request(req: RequestConfig): Promise<IHTTPResponse> {
		let ignoreBusy = false;

		if (req.headers && req.headers["ignoreBusy"]) {
			ignoreBusy = true;
			delete req.headers["ignoreBusy"];
		}

		if (!ignoreBusy) this.beginBusyRequest();

		let response: Response | null = null;
		let responseData: any = null;

		try {
			const headers: HeadersInit = {};

			// Build headers object, filtering out non-string values
			if (req.headers) {
				for (const key in req.headers) {
					const value = req.headers[key];
					if (typeof value === "string") {
						headers[key] = value;
					}
				}
			}

			// Prepare body
			let body: BodyInit | undefined;
			if (req.data) {
				if (req.data instanceof FormData) {
					body = req.data;
				} else if (typeof req.data === "object") {
					body = JSON.stringify(req.data);
					if (!headers["Content-Type"]) {
						headers["Content-Type"] = "application/json";
					}
				} else {
					body = req.data as BodyInit;
				}
			}

			// Build URL with query params
			let url = req.url;
			if (req.params) {
				const searchParams = new URLSearchParams();
				for (const key in req.params) {
					searchParams.append(key, String(req.params[key]));
				}
				const queryString = searchParams.toString();
				if (queryString) {
					url += (url.includes("?") ? "&" : "?") + queryString;
				}
			}

			response = await fetchFresh(url, {
				method: req.method ?? "GET",
				headers,
				body,
			});

			// Gateways commonly return HTML for temporary upstream failures. Do not
			// attempt to parse those bodies as API JSON or expose parser errors to users.
			if (TEMPORARY_SERVICE_STATUSES.has(response.status)) {
				responseData = null;
			} else {
				// Parse response based on Content-Type or responseType
				const contentType = response.headers.get("content-type");
				const responseType = req.responseType ?? "json";
				const isJsonContent = contentType?.includes("application/json") || contentType?.includes("+json");

				if (!response.ok && !isJsonContent) {
					responseData = await response.text();
				} else if (responseType === "json" || isJsonContent) {
					try {
						responseData = await response.json();
					} catch {
						if (!response.ok) responseData = null;
						else throw new Error("The service returned an invalid response. Please try again later.");
					}
				} else if (responseType === "text" || contentType?.includes("text/")) {
					responseData = await response.text();
				} else if (responseType === "blob") {
					responseData = await response.blob();
				} else if (responseType === "arrayBuffer") {
					responseData = await response.arrayBuffer();
				} else {
					// Default to json
					try {
						responseData = await response.json();
					} catch {
						responseData = await response.text();
					}
				}
			}
		} catch (e) {
			const err = e as Error;
			if (!ignoreBusy) this.endBusyRequest();

			return {
				error: { code: -1, message: err.message },
				status: 0,
				data: null,
			};
		}

		if (!ignoreBusy) this.endBusyRequest();

		if (!response) {
			return {
				error: { code: -2, message: "no response" },
				status: 0,
				data: null,
			};
		}

		// Convert Headers to plain object
		const headersObj: Record<string, string> = {};
		response.headers.forEach((value, key) => {
			headersObj[key] = value;
		});

		// Handle error responses
		if (!response.ok) {
			if (TEMPORARY_SERVICE_STATUSES.has(response.status)) {
				return {
					error: { code: response.status, message: TEMPORARY_SERVICE_MESSAGE },
					headers: headersObj,
					status: response.status,
					data: null,
				};
			}
			if (responseData && typeof responseData === "object" && responseData.error) {
				return {
					error: responseData.error,
					headers: headersObj,
					status: response.status,
					data: responseData.data ?? null,
				};
			}

			return {
				error: { code: response.status, message: response.statusText },
				headers: headersObj,
				status: response.status,
				data: responseData,
			};
		}

		// Handle success responses with API wrapper format
		if (responseData && typeof responseData === "object" && "responseTime" in responseData && "version" in responseData) {
			if (responseData.error) {
				return {
					headers: headersObj,
					status: response.status,
					error: responseData.error,
					data: null,
				};
			} else {
				return {
					headers: headersObj,
					status: response.status,
					data: responseData.data,
				};
			}
		}

		return {
			headers: headersObj,
			status: response.status,
			data: responseData,
		};
	}

	jsonToFormData(data: Record<string, any>, formData = new FormData(), parentKey = ""): FormData {
		for (const key in data) {
			if (!data.hasOwnProperty(key)) continue;

			const value = data[key];
			const fieldKey = parentKey ? `${parentKey}[${key}]` : key;

			if (value instanceof File) {
				formData.append(fieldKey, value);
			} else if (value instanceof Blob) {
				formData.append(fieldKey, value);
			} else if (typeof value === "object" && value !== null) {
				this.jsonToFormData(value, formData, fieldKey);
			} else {
				formData.append(fieldKey, String(value));
			}
		}
		return formData;
	}

	private beginBusyRequest() {
		this.activeBusyRequests += 1;
		if (this.activeBusyRequests === 1) {
			this.busy = true;
			GD.S_HTTP_BUSY.invoke(true);
		}
	}

	private endBusyRequest() {
		this.activeBusyRequests = Math.max(0, this.activeBusyRequests - 1);
		if (this.activeBusyRequests === 0) {
			this.busy = false;
			GD.S_HTTP_BUSY.invoke(false);
		}
	}
}

export default HTTP;
